import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NodeSubtype, NodeType, resolveNodeSubtype } from '../../types';
import { DEFAULT_SUBTYPE_BY_TYPE, NODE_SUBTYPE_OPTIONS_BY_TYPE, NODE_TYPE_OPTIONS } from './nodeTypeOptions';

interface NodeFormPopupProps {
  title: string;
  initialLabel: string;
  initialType: NodeType;
  initialSubtype?: NodeSubtype;
  allowDelete: boolean;
  onConfirm: (label: string, type: NodeType, subtype: NodeSubtype) => void;
  onDelete?: () => void;
  onClose: () => void;
  screenX: number;
  screenY: number;
}

const NodeFormPopup: React.FC<NodeFormPopupProps> = ({
  title,
  initialLabel,
  initialType,
  initialSubtype,
  allowDelete,
  onConfirm,
  onDelete,
  onClose,
  screenX,
  screenY,
}) => {
  const [label, setLabel] = useState(initialLabel);
  const [ntype, setNtype] = useState<NodeType>(initialType);
  const [subtype, setSubtype] = useState<NodeSubtype>(resolveNodeSubtype(initialType, initialSubtype));
  const inputRef = useRef<HTMLInputElement>(null);

  const subtypeOptions = useMemo(() => NODE_SUBTYPE_OPTIONS_BY_TYPE[ntype], [ntype]);

  useEffect(() => {
    setLabel(initialLabel);
    setNtype(initialType);
    setSubtype(resolveNodeSubtype(initialType, initialSubtype));
  }, [initialLabel, initialType, initialSubtype]);

  useEffect(() => {
    if (!subtypeOptions.some(option => option.subtype === subtype)) {
      setSubtype(DEFAULT_SUBTYPE_BY_TYPE[ntype]);
    }
  }, [ntype, subtype, subtypeOptions]);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      if (initialLabel) {
        inputRef.current?.select();
      }
    }, 40);

    return () => clearTimeout(timer);
  }, [title, initialLabel]);

  const confirm = () => onConfirm(label, ntype, subtype);

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirm();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        left: screenX,
        top: screenY,
        transform: 'translate(-50%, -110%)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        background: 'white',
        border: '1.5px solid #4a9e6b',
        borderRadius: 10,
        padding: '10px 12px',
        boxShadow: '0 4px 18px rgba(0,0,0,0.15)',
        zIndex: 200,
        minWidth: allowDelete ? 240 : 190,
      }}
      onClick={e => e.stopPropagation()}
    >
      <p
        style={{
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: '.07em',
          color: '#aaa',
          textAlign: 'center',
          margin: 0,
        }}
      >
        {title}
      </p>

      <div style={{ display: 'flex', gap: 5 }}>
        {NODE_TYPE_OPTIONS.map(nt => (
          <button
            key={nt.type}
            onClick={() => setNtype(nt.type)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              border: `1.5px solid ${ntype === nt.type ? '#4a9e6b' : '#eee'}`,
              borderRadius: 8,
              padding: '5px 3px',
              cursor: 'pointer',
              background: ntype === nt.type ? '#f0faf5' : 'white',
            }}
          >
            <span style={{ fontSize: 13 }}>{nt.icon}</span>
            <span
              style={{
                fontSize: 8,
                color: ntype === nt.type ? '#2d7a50' : '#888',
                fontWeight: ntype === nt.type ? 700 : 400,
                textTransform: 'capitalize',
              }}
            >
              {nt.label}
            </span>
          </button>
        ))}
      </div>

      {ntype !== 'event' && subtypeOptions.length > 1 && (
        <div style={{ display: 'flex', gap: 5 }}>
          {subtypeOptions.map(option => (
            <button
              key={option.subtype}
              onClick={() => setSubtype(option.subtype)}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                border: `1.5px solid ${subtype === option.subtype ? '#4a9e6b' : '#eee'}`,
                borderRadius: 8,
                padding: '5px 3px',
                cursor: 'pointer',
                background: subtype === option.subtype ? '#f0faf5' : 'white',
                fontSize: 9,
              }}
            >
              <span>{option.icon}</span>
              <span>{option.subtypeLabel}</span>
            </button>
          ))}
        </div>
      )}

      <input
        ref={inputRef}
        value={label}
        onChange={e => setLabel(e.target.value)}
        onKeyDown={handleKey}
        placeholder="nombre del nodo…"
        maxLength={24}
        style={{
          fontFamily: "'Caveat',cursive",
          fontSize: 15,
          textAlign: 'center',
          border: '1.5px solid #4a9e6b',
          borderRadius: 6,
          padding: '4px 8px',
          outline: 'none',
          width: '100%',
          color: '#333',
        }}
      />
      <p style={{ fontSize: 9, color: '#bbb', textAlign: 'center', margin: 0 }}>Enter · Esc para cancelar</p>
      {allowDelete && onDelete && (
        <button
          onClick={onDelete}
          style={{
            fontFamily: "'DM Mono',monospace",
            fontSize: 10,
            color: '#c0392b',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'center',
          }}
        >
          ✕ eliminar nodo
        </button>
      )}
    </div>
  );
};

export default NodeFormPopup;
